import { useState } from "react";
import { Alert, Badge, Card, DataTable, Field, Input, PageHeader, Select, Spinner } from "@empite/ui-components";
import type { Operator, Reason, TeamInvitation } from "../admin-client";
import { useCreateInvitation, useRevokeInvitation, useTeamInvitations, useTeamMembers } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, isPlatformStaff } from "./guards";
import { NotPermitted } from "./packages";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";

/**
 * The tenant's own seats.
 *
 * Deliberately NOT the platform operator directory: this screen reads
 * /admin/v1/tenant/members and /tenant/invites, whose store methods carry the
 * request's TenantScope in their WHERE clause, so a tenant owner sees their own
 * colleagues and no platform staff at all. The permission is team:manage, held
 * by tenant_owner and by super_admin and by NOBODY else — a tenant_member is
 * refused both reads with a 403, which is why the nav hides this link from
 * them. It is a different grant from admins:manage on purpose: holding one
 * does not imply the other, and the two point at different stores.
 *
 * There is no "add member" control, because there is no route behind one. A
 * seat is OFFERED and the account is born only when the invited person authors
 * their own password — a console that created an operator directly would put
 * the inviter in possession of a colleague's credential.
 */
const MANAGE = "team:manage";

/** The two seats a tenant has to hand out. Mirrored from internal/admin's
 *  TenantRoles to decide what to DRAW; the server re-checks membership and
 *  refuses anything else, platform roles included. The labels say what each
 *  one can CHANGE rather than which screens it sees, because that is the
 *  question the person filling this in is actually answering — tenant_member
 *  is the owner's list with every act removed. */
const DEFAULT_ROLE = "tenant_member";

const TENANT_ROLES: ReadonlyArray<{ role: string; label: string }> = [
  { role: "tenant_owner", label: "Owner — runs the company, and can invite" },
  { role: "tenant_member", label: "Member — read-only: sees the company, changes nothing" },
];

export function Team() {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, MANAGE) ? (
          <TeamScreen operator={session.operator} />
        ) : (
          <NotPermitted title="Team" permission={MANAGE} />
        )
      }
    </RequireOperator>
  );
}

function TeamScreen({ operator }: { operator: Operator }) {
  const members = useTeamMembers();
  const invites = useTeamInvitations();

  return (
    <>
      <PageHeader
        title="Team"
        description="Who holds a seat in this company's back office, and who has been offered one."
      />
      {isPlatformStaff(operator) ? (
        <Alert tone="warning" title="You belong to no tenant">
          These lists are a TENANT's seats. Signed in as platform staff you have no tenant for the
          server to resolve, so they will be empty and an invitation has to name the tenant it is
          for — which this form does not offer.
        </Alert>
      ) : null}
      <InviteForm />
      <Card title="Members">
        {members.isPending ? (
          <Spinner label="Loading the team" />
        ) : members.error ? (
          <ErrorAlert error={members.error} title="Could not load the team" />
        ) : (
          <DataTable<Operator>
            rows={members.data?.members ?? []}
            rowKey={(row) => row.id}
            empty="Nobody else holds a seat in this company yet."
            columns={[
              {
                key: "name",
                header: "Operator",
                render: (row) => (
                  <>
                    <strong>{row.display_name || row.email}</strong>
                    <div className="esm-muted-text">{row.email}</div>
                  </>
                ),
              },
              {
                key: "roles",
                header: "Roles",
                render: (row) => (
                  <>
                    {row.roles.map((role) => (
                      <Badge key={role}>{role}</Badge>
                    ))}
                  </>
                ),
              },
              { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
              { key: "created", header: "Joined", render: (row) => when(row.created_at) },
              {
                key: "seen",
                header: "Last signed in",
                render: (row) =>
                  row.last_signed_in_at ? (
                    when(row.last_signed_in_at)
                  ) : (
                    <span className="esm-muted-text">never</span>
                  ),
              },
            ]}
          />
        )}
      </Card>
      <Card title="Invitations">
        {invites.isPending ? (
          <Spinner label="Loading the invitations" />
        ) : invites.error ? (
          <ErrorAlert error={invites.error} title="Could not load the invitations" />
        ) : (
          <InvitationTable rows={invites.data?.invitations ?? []} />
        )}
      </Card>
    </>
  );
}

/**
 * Offering a seat.
 *
 * The write goes through ReasonButton rather than a submit, like every other
 * mutation in this console: the Admin API refuses an unsafe method with no
 * reason code, and an Enter key that posted past the prompt would be a
 * mutation with no reason on it.
 */
function InviteForm() {
  const invite = useCreateInvitation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>(DEFAULT_ROLE);
  // The raw token, shown ONCE. The list route cannot return it — the table
  // holds only its keyed hash — so if this is lost the only way to give
  // somebody a link is to revoke and re-invite.
  const [issued, setIssued] = useState<{ email: string; link: string } | null>(null);

  const address = email.trim();
  const ready = address !== "" && address.includes("@");

  async function run(reason: Reason) {
    const result = await invite.mutateAsync({ body: { email: address, role }, reason });
    setIssued({
      email: result.invitation.email,
      // Built against THIS origin, which is the console the invitee has to
      // land on. The server does not compose the link for the same reason:
      // internal/admin does not know where its console is deployed, so it
      // sends the path and the token separately.
      link: `${window.location.origin}/accept-invite?token=${encodeURIComponent(result.invite_token)}`,
    });
    setEmail("");
  }

  return (
    <Card title="Invite somebody">
      <ErrorAlert error={invite.error} title="That invitation was not sent" />
      {issued ? (
        <Alert tone="success" title={`Invitation sent to ${issued.email}`}>
          <p>
            It has been emailed. This link is the same offer, shown here once and never again —
            pass it on if the mail does not arrive:
          </p>
          <p>
            <code>{issued.link}</code>
          </p>
        </Alert>
      ) : null}
      <Field label="Their email" htmlFor="invite-email">
        <Input
          id="invite-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field
        label="Their role"
        htmlFor="invite-role"
        hint="An owner can invite and remove colleagues. A member cannot."
      >
        <Select id="invite-role" value={role} onChange={(event) => setRole(event.target.value)}>
          {TENANT_ROLES.map((entry) => (
            <option key={entry.role} value={entry.role}>
              {entry.label}
            </option>
          ))}
        </Select>
      </Field>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={invite.isPending}
          disabled={!ready}
          mutation={{
            title: `Invite ${address || "somebody"}`,
            tone: "primary",
            description: (
              <>
                Offers a seat in this company as <code>{role}</code>. They choose their own
                password; nobody here ever holds it.
              </>
            ),
            confirmLabel: "Send the invitation",
            run,
          }}
        >
          Send invitation
        </ReasonButton>
      </div>
    </Card>
  );
}

function InvitationTable({ rows }: { rows: TeamInvitation[] }) {
  const revoke = useRevokeInvitation();

  return (
    <>
      <ErrorAlert error={revoke.error} title="That invitation was not withdrawn" />
      <DataTable<TeamInvitation>
        rows={rows}
        rowKey={(row) => row.id}
        empty="No seat has been offered yet."
        columns={[
          { key: "email", header: "Invited", render: (row) => row.email },
          { key: "role", header: "Role", render: (row) => <Badge>{row.role}</Badge> },
          {
            key: "status",
            header: "Status",
            // `live` and not a comparison against expires_at: the server
            // resolved the expiry, and a browser whose clock is wrong would
            // otherwise call an invitation dead that the server still accepts.
            render: (row) =>
              row.status === "pending" && !row.live ? (
                <Badge tone="warning">expired</Badge>
              ) : (
                <StatusBadge status={row.status} />
              ),
          },
          {
            key: "invited_by",
            header: "Offered by",
            render: (row) =>
              row.invited_by_email ? (
                row.invited_by_email
              ) : (
                <span className="esm-muted-text">—</span>
              ),
          },
          { key: "expires", header: "Expires", render: (row) => when(row.expires_at) },
          {
            key: "actions",
            header: "",
            align: "end",
            // Only a LIVE offer can be withdrawn. An accepted one is a
            // colleague now, and a revoked or expired one is already over —
            // the server answers all three the same way, and offering the
            // button anyway teaches operators to click through refusals.
            render: (row) =>
              row.status === "pending" && row.live ? (
                <ReasonButton
                  busy={revoke.isPending}
                  mutation={{
                    title: `Withdraw the invitation to ${row.email}`,
                    description: (
                      <>
                        The link stops working. The row stays, marked revoked, so the trail keeps
                        saying a seat was offered and taken back. You can invite the same address
                        again afterwards.
                      </>
                    ),
                    confirmLabel: "Withdraw it",
                    run: (reason: Reason) =>
                      revoke.mutateAsync({ inviteID: row.id, reason }).then(() => undefined),
                  }}
                >
                  Withdraw
                </ReasonButton>
              ) : null,
          },
        ]}
      />
    </>
  );
}
